import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Download, History, Loader2, RefreshCw, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { validateExcelImportFile } from "@/lib/excelImport";
import { XeroStatusError } from "@/components/admin/XeroStatusStates";
import XeroMappingRow from "@/components/admin/XeroMappingRow";
import {
  XERO_DIM_CUBE_MAPPINGS_QUERY_KEY,
  bulkImportDimCubeMappings,
  exportDimCubeMappings,
  getDimCubeMappings,
  type DimCubeMappingsSummary,
} from "@/services/xeroApi";

function MappingsErrorBanner({ data }: { data: DimCubeMappingsSummary }) {
  if (!data.mappings_error) return null;
  return (
    <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <p>{data.mappings_error}</p>
    </div>
  );
}


export default function XeroMappingsTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const mappings = useQuery({
    queryKey: XERO_DIM_CUBE_MAPPINGS_QUERY_KEY,
    queryFn: getDimCubeMappings,
    refetchInterval: 15_000,
  });
  const [isExporting, setIsExporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const bulkImport = useMutation({
    mutationFn: bulkImportDimCubeMappings,
    onSuccess: (result) => {
      toast({
        title: "Mappings imported",
        description:
          result.updated_count === 0
            ? "The file matched what Prism already holds, so nothing changed."
            : `${result.updated_count} row(s) updated.`,
      });
      queryClient.invalidateQueries({ queryKey: XERO_DIM_CUBE_MAPPINGS_QUERY_KEY });
    },
    onError: (e) => {
      toast({
        variant: "destructive",
        title: "Couldn’t import the file",
        description: (e as Error).message,
      });
    },
  });

  const handleImportClick = () => {
    if (bulkImport.isPending) return;
    fileInputRef.current?.click();
  };

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const problem = validateExcelImportFile(file);
    if (problem) {
      toast({ variant: "destructive", title: "Couldn’t import the file", description: problem });
      return;
    }
    bulkImport.mutate(file);
  };

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      await exportDimCubeMappings();
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Couldn’t export the mappings",
        description: (e as Error).message,
      });
    } finally {
      setIsExporting(false);
    }
  };

  if (mappings.isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading cube mappings…
      </div>
    );
  }

  if (mappings.isError) {
    return (
      <XeroStatusError
        message={
          (mappings.error as Error)?.message ??
          "Couldn’t load the Xero cube mappings."
        }
        onRetry={() => mappings.refetch()}
      />
    );
  }

  const rows = mappings.data?.mappings ?? [];

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-foreground">
          Mappings
          {rows.length > 0 && (
            <span className="ml-2 font-normal text-muted-foreground">
              {rows.length} {rows.length === 1 ? "row" : "rows"}
            </span>
          )}
        </h3>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => mappings.refetch()}
            disabled={mappings.isFetching}
            title="Refresh"
            aria-label="Refresh mappings"
          >
            <RefreshCw className={`h-4 w-4 ${mappings.isFetching ? "animate-spin" : ""}`} />
          </Button>
          {/* Opens in this browser tab; the log page's Back link (or the
              browser's Back button) returns to this tab. */}
          <Button size="sm" variant="outline" className="gap-2" asChild>
            <Link to="/datacore/xero/mappings/logs">
              <History className="h-4 w-4" />
              Logs
            </Link>
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx"
            className="hidden"
            onChange={handleFileSelected}
          />
          <Button
            size="sm"
            variant="outline"
            className="gap-2"
            onClick={handleImportClick}
            disabled={bulkImport.isPending}
          >
            {bulkImport.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            {bulkImport.isPending ? "Importing…" : "Import from Excel"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="gap-2"
            onClick={handleExport}
            disabled={isExporting || rows.length === 0}
          >
            {isExporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            {isExporting ? "Exporting…" : "Export to Excel"}
          </Button>
        </div>
      </div>

      {mappings.data && <MappingsErrorBanner data={mappings.data} />}

      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          datamartxero.dim_cube_mappings has no rows yet.
        </p>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/40 hover:bg-muted/40">
                <TableHead className="w-[220px]">Legacy office fetched</TableHead>
                <TableHead className="w-[220px]">Legacy office</TableHead>
                <TableHead className="w-[220px]">Legacy office reported</TableHead>
                <TableHead className="w-[220px]">Site</TableHead>
                <TableHead className="w-[220px]">Legacy location reported</TableHead>
                <TableHead className="w-[200px]">Mapping perimeter</TableHead>
                <TableHead
                  className="w-[170px]"
                  title="Saving this also updates every other row sharing the same Legacy office."
                >
                  Xero inclusion date
                </TableHead>
                <TableHead className="w-[170px]">Updated (Fabric)</TableHead>
                <TableHead className="w-[170px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((mapping) => (
                <XeroMappingRow
                  key={`${mapping.row_id ?? ""}:${mapping.legacy_office_fetched}:${mapping.legacy_office ?? ""}:${mapping.legacy_office_reported ?? ""}:${mapping.site ?? ""}:${mapping.legacy_location_reported ?? ""}:${mapping.mapping_perimeter ?? ""}:${mapping.xero_inclusion_date ?? ""}`}
                  mapping={mapping}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
